// components/exercises/lesson/ChordIdentification.tsx
import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Box, Button, Text } from "@chakra-ui/react";
import DynamicPiano, {
  DynamicPianoHandle,
} from "components/exercises/DynamicPiano";
import { IoMdPlayCircle } from "react-icons/io";
import PlayButton from "components/PlayButton";

import { ExerciseDefinition } from "utils/exercises/types";
import { useSound } from "providers/SoundProvider";

interface ChordIdentificationProps {
  exercise: ExerciseDefinition;
  onResult: (isCorrect: boolean) => void;
}

const ChordIdentification = forwardRef(
  (props: ChordIdentificationProps, ref) => {
    const { exercise, onResult } = props;
    const [interactionAllowed, setInteractionAllowed] = useState(true); // State to manage interaction with the piano

    const pianoRef = useRef<DynamicPianoHandle>(null);
    const { playChord, isAudioPlaying, setIsAudioPlaying } = useSound();

    const playChordNotes = async () => {
      if (!isAudioPlaying) {
        setIsAudioPlaying(true);

        const { notes } = exercise.data;
        if (notes && notes.length) {
          playChord(notes);
        }

        await new Promise((resolve) => setTimeout(resolve, 3000));
        setIsAudioPlaying(false);
      }
    };

    useEffect(() => {
      setInteractionAllowed(true);
      if (!isAudioPlaying) {
        playChordNotes();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [exercise.data.notes]); // Re-run when notes change

    useImperativeHandle(ref, () => ({
      checkAnswer: () => localCheckAnswer(),
    }));

    const localCheckAnswer = () => {
      if (pianoRef.current) {
        const userSelectedNotes = pianoRef.current.getUserSelectedNotes();
        const sortedUserNotes = [...userSelectedNotes].sort((a, b) => a - b); // Clone before sorting to prevent mutations
        const localExerciseDataNotes = exercise.data.notes || [];
        const sortedCorrectNotes = [...localExerciseDataNotes].sort(
          (a, b) => a - b
        ); // Clone and sort to prevent mutating original data

        // Check if the number of user-selected notes matches the number of correct notes
        const isCorrect =
          userSelectedNotes.length === sortedCorrectNotes.length &&
          sortedUserNotes.every(
            (note, index) => note === sortedCorrectNotes[index]
          );

        onResult(isCorrect); // Communicate the result to the parent component via onResult
        setInteractionAllowed(false); // Disable interaction after checking the answer
      }
    };

    return (
      <Box textAlign="center" width={"100%"}>
        <Text fontSize="xl" fontWeight={500} mb="4">
          Listen to the chord and select the notes on the piano
        </Text>
        <PlayButton label="Play Chord" onClick={playChordNotes} />
        <Box mb={6}></Box>

        <DynamicPiano
          ref={pianoRef}
          exerciseDefinition={exercise}
          disabled={!interactionAllowed}
          correctNotes={exercise.data.notes || []}
        />
      </Box>
    );
  }
);

export default ChordIdentification;
